// Cost estimation for WhatsApp chat analysis
// Pricing model based on OpenAI API costs (×10 markup, rounded up)

interface CostEstimate {
  totalCalls: number;
  breakdown: {
    imageAnalysis: number;
    audioTranscription: number;
    videoAnalysis: number;
    documentAnalysis: number;
    contactAnalysis: number;
    summaryGeneration: number;
  };
  estimatedCost: number;
  costBreakdown: {
    images: number;
    audio: number;
    video: number;
    documents: number;
    contacts: number;
    summary: number;
  };
}

// Pricing model based on OpenAI API costs (×10 markup, rounded up)
const PRICING = {
  // Chat summary using GPT-5-mini (~$0.002 base cost × 10 = $0.02, rounded to $0.05)
  baseCost: 0.05,
  
  // Per-media costs (OpenAI cost × 10, rounded up)
  imageCost: 0.25,      // GPT-4 Vision: ~$0.025 → $0.25
  audioCost: 0.10,      // Whisper: $0.006/min → $0.06, rounded to $0.10
  videoCost: 0.50,      // Video analysis: ~$0.05 → $0.50
  documentCost: 0.10,   // Document parsing: ~$0.01 → $0.10
  contactCost: 0.05,    // Contact parsing: ~$0.005 → $0.05
};

export function estimateCost(
  imageCount: number,
  audioCount: number,
  includesSummary: boolean = true,
  isAdmin: boolean = false,
  videoCount: number = 0,
  documentCount: number = 0,
  contactCount: number = 0
): CostEstimate {
  // Calculate costs based on OpenAI pricing (×10 markup, rounded up)
  const imageTotalCost = imageCount * PRICING.imageCost;
  const audioTotalCost = audioCount * PRICING.audioCost;
  const videoTotalCost = videoCount * PRICING.videoCost;
  const documentTotalCost = documentCount * PRICING.documentCost;
  const contactTotalCost = contactCount * PRICING.contactCost;
  const summaryTotalCost = includesSummary ? PRICING.baseCost : 0;

  const totalCalls = imageCount + audioCount + videoCount + documentCount + contactCount + (includesSummary ? 1 : 0);
  const estimatedCost = imageTotalCost + audioTotalCost + videoTotalCost + documentTotalCost + contactTotalCost + summaryTotalCost;

  return {
    totalCalls,
    breakdown: {
      imageAnalysis: imageCount,
      audioTranscription: audioCount,
      videoAnalysis: videoCount,
      documentAnalysis: documentCount,
      contactAnalysis: contactCount,
      summaryGeneration: includesSummary ? 1 : 0,
    },
    estimatedCost,
    costBreakdown: {
      images: imageTotalCost,
      audio: audioTotalCost,
      video: videoTotalCost,
      documents: documentTotalCost,
      contacts: contactTotalCost,
      summary: summaryTotalCost,
    },
  };
}

export function estimateChatQueryCost(messageCount: number, isAdmin: boolean = false): number {
  // Chat query costs $0.10 per query
  return 0.10;
}

export function estimateRerunCaptionCost(attachmentType: 'image' | 'audio' | 'video' | 'document' | 'contact', isAdmin: boolean = false): number {
  // Rerun costs the same as initial processing
  switch (attachmentType) {
    case 'image':
      return PRICING.imageCost;
    case 'audio':
      return PRICING.audioCost;
    case 'video':
      return PRICING.videoCost;
    case 'document':
      return PRICING.documentCost;
    case 'contact':
      return PRICING.contactCost;
    default:
      return PRICING.imageCost; // fallback
  }
}

export function formatCost(cost: number): string {
  if (cost < 0.01) {
    return `$${(cost * 100).toFixed(2)}¢`;
  }
  return `$${cost.toFixed(2)}`;
}
